/**
 * Ce que la tuile « situation financière » lit en base.
 *
 * ## La limite de lignes, et ce qu'elle impose
 *
 * PostgREST rend **1 000 lignes au maximum** par requête. Il y a 2 386
 * familles. Charger la liste pour l'additionner côté écran donnerait donc un
 * total faux de 58 %, sans erreur, sans message — et faux **dans le sens qui
 * rassure**, puisqu'il manquerait de la dette.
 *
 * D'où deux lectures distinctes, et jamais une seule :
 *
 * - `loadTotals()` interroge `v_financial_situation_totals`, **une ligne**, qui
 *   somme en base.
 * - `loadFamilies()` pagine `v_user_financial_situation` par tranches de 25,
 *   avec `count: 'exact'` pour savoir combien il en reste.
 *
 * Un total affiché est ainsi soit celui de la base, soit absent.
 */

import { supabase } from '../supabase';
import { db } from '../payments/db';
import { messageErreur } from '../payments/erreur';
import type { FamilySituation } from './situation';

/** Les totaux, tels que la vue d'une ligne les rend. */
export interface FinancialTotals {
  familyCount: number;
  invoicedFamilyCount: number;
  overdueFamilyCount: number;
  unpaidFamilyCount: number;
  partialFamilyCount: number;
  settledFamilyCount: number;
  provisionFamilyCount: number;
  legacyGapFamilyCount: number;
  invoicedCents: number;
  creditNotesCents: number;
  netCents: number;
  receivedCents: number;
  outstandingCents: number;
  cancelledOutstandingCents: number;
  provisionCents: number;
  legacyPaidCents: number;
  legacyPaidInvoiceCount: number;
  dueSoonCents: number;
  overdue30Cents: number;
  overdue90Cents: number;
  overdue365Cents: number;
  overdueBeyondCents: number;
  maxDaysOverdue: number;
  /**
   * Ce que les totaux ont ÉCARTÉ : les factures marquées `hors_comptabilite`.
   *
   * Un total qui retranche sans le dire est aussi trompeur que celui qui
   * comptait. Mesuré le 20 septembre 2026 : 42 020,00 € sur 25 factures de
   * test, qui rendaient le total des notes de crédit faux à 43 %.
   */
  horsComptabiliteCents: number;
  horsComptabiliteCount: number;
}

const n = (v: unknown) => {
  const x = typeof v === 'number' ? v : Number(v ?? 0);
  return Number.isFinite(x) ? x : 0;
};

export async function loadTotals(): Promise<FinancialTotals> {
  const { data, error } = await supabase
    .from('v_financial_situation_totals')
    .select('*')
    .maybeSingle();

  if (error) throw new Error(messageErreur(error, 'Totaux illisibles'));
  const r = (data ?? {}) as Record<string, unknown>;
  return {
    familyCount: n(r.family_count),
    invoicedFamilyCount: n(r.invoiced_family_count),
    overdueFamilyCount: n(r.overdue_family_count),
    unpaidFamilyCount: n(r.unpaid_family_count),
    partialFamilyCount: n(r.partial_family_count),
    settledFamilyCount: n(r.settled_family_count),
    provisionFamilyCount: n(r.provision_family_count),
    legacyGapFamilyCount: n(r.legacy_gap_family_count),
    invoicedCents: n(r.invoiced_cents),
    creditNotesCents: n(r.credit_notes_cents),
    netCents: n(r.net_cents),
    receivedCents: n(r.received_cents),
    outstandingCents: n(r.outstanding_cents),
    cancelledOutstandingCents: n(r.cancelled_outstanding_cents),
    provisionCents: n(r.provision_cents),
    legacyPaidCents: n(r.legacy_paid_cents),
    legacyPaidInvoiceCount: n(r.legacy_paid_invoice_count),
    dueSoonCents: n(r.due_soon_cents),
    overdue30Cents: n(r.overdue_30_cents),
    overdue90Cents: n(r.overdue_90_cents),
    overdue365Cents: n(r.overdue_365_cents),
    overdueBeyondCents: n(r.overdue_beyond_cents),
    maxDaysOverdue: n(r.max_days_overdue),
    horsComptabiliteCents: n(r.hors_comptabilite_cents),
    horsComptabiliteCount: n(r.hors_comptabilite_count),
  };
}

export type TriFamilles = 'reste' | 'retard' | 'nom' | 'facture';

export interface FiltreFamilles {
  /** Cherche dans le nom, le prénom, l'e-mail. */
  recherche: string;
  /** `''` pour toutes. */
  situation: string;
  /** N'afficher que les familles dont l'ancien circuit sait plus. */
  ecartSeulement: boolean;
  tri: TriFamilles;
  page: number;
  parPage: number;
}

export interface PageFamilles {
  familles: FamilySituation[];
  total: number;
  pages: number;
}

const CHAMPS = `
  user_id, prenom, nom, email, account_type, organisation_name,
  invoice_count, invoiced_cents, credit_notes_cents, net_cents, received_cents,
  outstanding_cents, cancelled_outstanding_cents, overpaid_cents, unpaid_count,
  provision_cents, registrations_count, last_payment_date, oldest_due_date,
  days_overdue, legacy_paid_cents, legacy_paid_invoice_count, legacy_ahead, situation
`;

const ORDRE: Record<TriFamilles, { colonne: string; ascendant: boolean }> = {
  reste: { colonne: 'outstanding_cents', ascendant: false },
  retard: { colonne: 'days_overdue', ascendant: false },
  nom: { colonne: 'nom', ascendant: true },
  facture: { colonne: 'invoiced_cents', ascendant: false },
};

export async function loadFamilies(f: FiltreFamilles): Promise<PageFamilles> {
  let requete = supabase
    .from('v_user_financial_situation')
    .select(CHAMPS, { count: 'exact' });

  const terme = f.recherche.trim();
  if (terme !== '') {
    /*
      `or` avec `ilike` sur trois colonnes. Les virgules et les parenthèses ont
      un sens dans la syntaxe de PostgREST : un terme qui en contient casserait
      le filtre — silencieusement, en cherchant autre chose que ce qui a été
      tapé. Ils sont donc retirés du terme.
    */
    const propre = terme.replace(/[(),*]/g, ' ').trim();
    if (propre !== '') {
      requete = requete.or(
        `nom.ilike.%${propre}%,prenom.ilike.%${propre}%,email.ilike.%${propre}%,organisation_name.ilike.%${propre}%`,
      );
    }
  }

  if (f.situation !== '') requete = requete.eq('situation', f.situation);
  if (f.ecartSeulement) requete = requete.eq('legacy_ahead', true);

  const o = ORDRE[f.tri];
  const debut = Math.max(0, (f.page - 1) * f.parPage);
  const { data, error, count } = await requete
    .order(o.colonne, { ascending: o.ascendant, nullsFirst: false })
    // Un second critère : sans lui, deux familles à égalité changent d'ordre
    // d'une page à l'autre, et une ligne peut apparaître deux fois ou jamais.
    .order('user_id', { ascending: true })
    .range(debut, debut + f.parPage - 1);

  if (error) throw new Error(messageErreur(error, 'Liste des familles illisible'));

  const familles: FamilySituation[] = (data ?? []).map((r: Record<string, unknown>) => versFamille(r));

  const total = count ?? familles.length;
  return { familles, total, pages: Math.max(1, Math.ceil(total / f.parPage)) };
}

/**
 * La situation d'UNE famille.
 *
 * Même vue, mêmes colonnes, même projection que la liste : deux lectures qui
 * divergeraient afficheraient deux restes dûs pour la même famille.
 *
 * Elle n'est PAS appelée à l'ouverture de la fiche. Mesurée à 39 ms, mais avec
 * un parcours complet de `credit_notes` et de `payments` que le filtre par
 * `user_id` ne traverse pas. Ces chiffres se paient au second clic, quand ils
 * sont demandés.
 */
export async function loadFamily(userId: string): Promise<FamilySituation | null> {
  /* Le client re-typé : `v_user_financial_situation` n'est pas dans
     `database.types.ts`, et `.eq('user_id', …)` n'y compile donc pas. Les
     lectures voisines de ce fichier portent la même dette ; celle-ci ne
     l'ajoute pas. */
  const { data, error } = await db
    .from('v_user_financial_situation')
    .select(CHAMPS)
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw new Error(messageErreur(error, 'Situation de la famille illisible'));
  return data ? versFamille(data as Record<string, unknown>) : null;
}

function versFamille(r: Record<string, unknown>): FamilySituation {
  return ({
    userId: String(r.user_id ?? ''),
    prenom: (r.prenom as string | null) ?? null,
    nom: (r.nom as string | null) ?? null,
    email: (r.email as string | null) ?? null,
    accountType: (r.account_type as string | null) ?? null,
    organisationName: (r.organisation_name as string | null) ?? null,
    invoiceCount: n(r.invoice_count),
    invoicedCents: n(r.invoiced_cents),
    creditNotesCents: n(r.credit_notes_cents),
    netCents: n(r.net_cents),
    receivedCents: n(r.received_cents),
    outstandingCents: n(r.outstanding_cents),
    cancelledOutstandingCents: n(r.cancelled_outstanding_cents),
    overpaidCents: n(r.overpaid_cents),
    unpaidCount: n(r.unpaid_count),
    provisionCents: n(r.provision_cents),
    registrationsCount: n(r.registrations_count),
    lastPaymentDate: (r.last_payment_date as string | null) ?? null,
    oldestDueDate: (r.oldest_due_date as string | null) ?? null,
    daysOverdue: n(r.days_overdue),
    legacyPaidCents: n(r.legacy_paid_cents),
    legacyPaidInvoiceCount: n(r.legacy_paid_invoice_count),
    legacyAhead: r.legacy_ahead === true,
    situation: String(r.situation ?? ''),
  });
}

/** Les factures d'une famille, pour la ligne dépliée. */
export interface InvoiceLine {
  invoiceId: string;
  invoiceNumber: string;
  status: string;
  settlement: string;
  amountCents: number;
  creditNotesCents: number;
  outstandingCents: number;
  allocatedCents: number;
  dueDate: string | null;
  pdfUrl: string | null;
}

export async function loadFamilyInvoices(userId: string): Promise<InvoiceLine[]> {
  const { data, error } = await supabase
    .from('v_invoice_balances')
    .select('invoice_id, invoice_number, status, settlement, amount_cents, credit_notes_cents, outstanding_cents, allocated_cents, due_date, pdf_url')
    .eq('user_id', userId)
    .order('due_date', { ascending: true, nullsFirst: false })
    .limit(200);

  if (error) throw new Error(messageErreur(error, 'Factures illisibles'));
  return (data ?? []).map((r: Record<string, unknown>) => ({
    invoiceId: String(r.invoice_id ?? ''),
    invoiceNumber: String(r.invoice_number ?? ''),
    status: String(r.status ?? ''),
    settlement: String(r.settlement ?? ''),
    amountCents: n(r.amount_cents),
    creditNotesCents: n(r.credit_notes_cents),
    outstandingCents: n(r.outstanding_cents),
    allocatedCents: n(r.allocated_cents),
    dueDate: (r.due_date as string | null) ?? null,
    pdfUrl: (r.pdf_url as string | null) ?? null,
  }));
}

/** Une note de crédit de la famille, avec ce qu'elle vaut encore. */
export interface CreditNoteLine {
  creditNoteId: string;
  creditNoteNumber: string;
  createdAt: string | null;
  /** La facture qu'elle allège. Nulle pour la note qui n'en a aucune. */
  invoiceNumber: string | null;
  amountCents: number;
  /** Ce qu'un virement lui a déjà rendu. */
  refundedCents: number;
  /** Ce qu'elle peut encore recevoir : montant moins déjà rendu. */
  refundableCents: number;
  /** Ce qu'il y a réellement à rendre, l'argent encaissé pris en compte. */
  aRendreCents: number;
  /**
   * L'argent qui existe DERRIÈRE la note : ce que la facture a encaissé et que
   * la note libère. Zéro veut dire que rien n'a jamais été reçu — la note
   * s'affiche comme remboursable, et le rembourser ferait sortir de l'argent
   * que la famille n'a pas versé. Mesuré le 20 septembre 2026 : 927 notes dans
   * ce cas, pour 90 955,25 €.
   */
  cashAdosseCents: number;
  destinee: string | null;
  status: string | null;
  type: string | null;
  pdfUrl: string | null;
}

/**
 * Les notes de crédit d'une famille, pour les retrouver.
 *
 * Demandé le 20 septembre 2026 : « dans les comptes familles il faudrait
 * pouvoir voir les notes de crédit rattachées pour les retrouver facilement ».
 * La ligne dépliée montrait un montant de notes par facture — un total sans
 * numéro, qu'aucune recherche ne rattrape.
 *
 * ## Lues par la FAMILLE, pas par la facture
 *
 * `v_invoice_credit_note_list` est indexée sur la facture, et la note qui n'en
 * a aucune y disparaît. Il en existe une en production, et c'est justement
 * celle qu'on cherche le plus. `v_credit_note_balances` porte `user_id` : les
 * 1 024 notes des 438 familles y sont toutes.
 *
 * ## Deux lectures, parce que le PDF manque à la vue
 *
 * `v_credit_note_balances` ne porte pas `pdf_url`, et une note qu'on ne peut
 * pas ouvrir n'est qu'à moitié retrouvée. Plutôt que de modifier une vue dont
 * six écrans dépendent, la table est relue en parallèle pour cette seule
 * colonne. Mesuré : 72 ms pour la vue, une fois, au dépliage.
 */
export async function loadFamilyCreditNotes(userId: string): Promise<CreditNoteLine[]> {
  const [soldes, pdfs] = await Promise.all([
    supabase
      .from('v_credit_note_balances')
      .select('credit_note_id, credit_note_number, created_at, invoice_number, amount_cents, '
        + 'refunded_cents, refundable_cents, a_rendre_cents, cash_adosse_cents, destinee, status, type')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(200),
    supabase
      .from('credit_notes')
      .select('id, pdf_url')
      .eq('user_id', userId)
      .limit(200),
  ]);
  if (soldes.error) throw new Error(messageErreur(soldes.error, 'Notes de crédit illisibles'));
  if (pdfs.error) throw new Error(messageErreur(pdfs.error, 'Notes de crédit illisibles'));

  const parPdf = new Map(
    ((pdfs.data ?? []) as unknown as { id: string; pdf_url: string | null }[])
      .map((c) => [c.id, c.pdf_url]),
  );

  return ((soldes.data ?? []) as unknown as Record<string, unknown>[]).map((r) => ({
    creditNoteId: String(r.credit_note_id ?? ''),
    creditNoteNumber: String(r.credit_note_number ?? ''),
    createdAt: (r.created_at as string | null) ?? null,
    invoiceNumber: (r.invoice_number as string | null) ?? null,
    amountCents: n(r.amount_cents),
    refundedCents: n(r.refunded_cents),
    refundableCents: n(r.refundable_cents),
    aRendreCents: n(r.a_rendre_cents),
    cashAdosseCents: n(r.cash_adosse_cents),
    destinee: (r.destinee as string | null) ?? null,
    status: (r.status as string | null) ?? null,
    type: (r.type as string | null) ?? null,
    pdfUrl: parPdf.get(String(r.credit_note_id ?? '')) ?? null,
  }));
}
